import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Radio } from "lucide-react";

const LIVE_TOKENS = ["Live", "In Progress", "LIVE", "CRIT"];
const FINAL_TOKENS = ["Final", "FINAL", "final", "post", "OFF"];

function isGameFinal(g) {
  const s = `${g.state || ""} ${g.detail || ""}`;
  return FINAL_TOKENS.some((t) => s.includes(t));
}
function isGameLive(g) {
  if (isGameFinal(g)) return false;
  const stateRaw = (g.state || "").trim();
  if (stateRaw === "in") return true; // ESPN NFL live
  const s = `${stateRaw} ${g.detail || ""}`;
  return LIVE_TOKENS.some((t) => s.includes(t));
}

// Show LAA + ANA today only when they have a game scheduled/live/final
export default function MyTeamsScores() {
  const [games, setGames] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const prevScoresRef = useRef({});
  const [flashKeys, setFlashKeys] = useState({});

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    const load = async () => {
      try {
        const r = await api.get("/scores");
        if (cancelled) return;
        const all = [...(r.data.mlb || []), ...(r.data.nhl || [])];
        const mine = all.filter((g) =>
          g.away === "LAA" || g.home === "LAA" || g.away === "ANA" || g.home === "ANA"
        );

        // Detect score change → flash
        const nextFlash = {};
        mine.forEach((g, i) => {
          const key = `${g.league}-${i}`;
          const prev = prevScoresRef.current[key];
          const curr = `${g.away_score ?? ""}-${g.home_score ?? ""}`;
          if (prev !== undefined && prev !== curr) nextFlash[key] = Date.now();
          prevScoresRef.current[key] = curr;
        });
        if (Object.keys(nextFlash).length) {
          setFlashKeys((f) => ({ ...f, ...nextFlash }));
          setTimeout(() => setFlashKeys({}), 2500);
        }

        setGames(mine);
        setUpdatedAt(new Date());

        // Fast poll while any of my teams is live, otherwise slow poll
        const anyLive = mine.some(isGameLive);
        const nextDelay = anyLive ? 25000 : 3 * 60 * 1000;
        timeoutId = setTimeout(load, nextDelay);
      } catch {
        timeoutId = setTimeout(load, 60000);
      }
    };
    load();

    // 1-sec ticker so "Updated Xs ago" stays fresh
    const tick = setInterval(() => setNowTick(Date.now()), 1000);
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      clearInterval(tick);
    };
  }, []);

  if (games.length === 0) return null;

  const anyLive = games.some(isGameLive);
  const secondsAgo = updatedAt ? Math.max(0, Math.floor((nowTick - updatedAt.getTime()) / 1000)) : null;
  const updatedLabel = secondsAgo === null
    ? ""
    : secondsAgo < 5 ? "just now" : `updated ${secondsAgo}s ago`;

  return (
    <section
      className="bg-halo-navy text-halo-cream py-8 px-6 md:px-12 lg:px-16 border-b-2 border-halo-red"
      data-testid="my-teams-scores"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-halo-red font-sports uppercase tracking-[0.3em] text-xs">
            <Radio className={`w-3.5 h-3.5 ${anyLive ? "animate-pulse-live" : ""}`} strokeWidth={2.5} />
            <span>Our Teams — Today</span>
          </div>
          {updatedAt && (
            <div
              className="font-sports uppercase tracking-widest text-[10px] text-halo-cream/50"
              data-testid="scores-updated-at"
            >
              {updatedLabel}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {games.map((g, i) => {
            const key = `${g.league}-${i}`;
            const isAngels = g.league === "MLB";
            const live = isGameLive(g);
            const final = isGameFinal(g);
            const stateLabel = live
              ? (g.detail && !g.detail.match(/^live$/i) ? g.detail.toUpperCase() : "LIVE")
              : final ? "FINAL" : (g.detail || "SCHEDULED").toUpperCase();
            const accent = isAngels ? "border-halo-red" : "border-[#F47A38]";
            const teamLabel = isAngels ? "⚾ Angels Game" : "🏒 Ducks Game";
            const hasScores = g.away_score !== null && g.home_score !== null && g.away_score !== undefined && g.home_score !== undefined;
            const flashed = !!flashKeys[key];
            const gameUrl = g.game_url || (isAngels ? "https://www.espn.com/mlb/scoreboard" : "https://www.espn.com/nhl/scoreboard");
            return (
              <a
                key={key}
                href={gameUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`myteam-game-${g.league}`}
                title={`${live ? "Watch live" : final ? "Full recap" : "Game preview"}`}
                className={`bg-halo-navyLight border-l-4 ${accent} p-5 flex items-center justify-between gap-4 transition-colors duration-700 hover:bg-halo-navyLight/70 cursor-pointer ${flashed ? "bg-halo-red/20" : ""}`}
              >
                <div className="flex-1">
                  <div className="font-sports uppercase tracking-widest text-[10px] text-halo-cream/60 mb-2">{teamLabel}</div>
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <div className="font-sports uppercase text-xl">{g.away}</div>
                      <div className={`font-sports text-3xl mt-1 ${flashed ? "text-halo-red" : ""}`}>{hasScores ? g.away_score : "—"}</div>
                    </div>
                    <div className="font-sports text-halo-cream/40 text-lg">@</div>
                    <div className="text-center">
                      <div className="font-sports uppercase text-xl">{g.home}</div>
                      <div className={`font-sports text-3xl mt-1 ${flashed ? "text-halo-red" : ""}`}>{hasScores ? g.home_score : "—"}</div>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <span
                    className={`inline-block font-sports uppercase tracking-widest text-[10px] px-2 py-1 ${
                      live ? "bg-halo-red text-white animate-pulse-live" : final ? "bg-halo-navy text-halo-cream" : "border border-halo-cream/40 text-halo-cream/70"
                    }`}
                    data-testid={`myteam-state-${g.league}`}
                  >
                    {stateLabel}
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
